import { useState, useEffect } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { DollarSign, TrendingUp, ShoppingCart, Image, Wallet, Pencil, Check, X, Star } from "lucide-react";
import { BuyStarsModal, BUY_STARS_REOPEN_KEY } from "@/components/game/BuyStarsModal";
import cyberBg from "@/assets/game/cyber-topsellers.png";

export default function GameDashboard() {
  const { session, player, loading, signOut, refreshPlayer, updateDisplayName } = useGameAuth();
  const [myNfts, setMyNfts] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [showBuyStars, setShowBuyStars] = useState(false);

  useEffect(() => {
    if (player) {
      fetchMyNfts();
      fetchTransactions();
    }
  }, [player]);

  // Riapri automaticamente il modale Buy Stars al ritorno dal wallet (deeplink mobile)
  useEffect(() => {
    if (!player) return;
    try {
      const raw = sessionStorage.getItem(BUY_STARS_REOPEN_KEY);
      if (!raw) return;
      const intent = JSON.parse(raw);
      if (intent.playerId === player.id && Date.now() - intent.timestamp < 10 * 60 * 1000) {
        setShowBuyStars(true);
      }
      sessionStorage.removeItem(BUY_STARS_REOPEN_KEY);
    } catch {
      sessionStorage.removeItem(BUY_STARS_REOPEN_KEY);
    }
  }, [player]);

  const fetchMyNfts = async () => {
    if (!player) return;
    const { data } = await supabase
      .from("game_nfts")
      .select("*")
      .eq("owner_id", player.id)
      .order("created_at", { ascending: false });
    setMyNfts(data || []);
  };

  const fetchTransactions = async () => {
    if (!player) return;
    const { data } = await supabase
      .from("game_transactions")
      .select("*")
      .or(`buyer_id.eq.${player.id},seller_id.eq.${player.id}`)
      .order("created_at", { ascending: false })
      .limit(20);
    setTransactions(data || []);
  };

  const toggleListing = async (nftId: string, currentlyListed: boolean) => {
    await supabase
      .from("game_nfts")
      .update({ is_listed: !currentlyListed })
      .eq("id", nftId);
    fetchMyNfts();
  };

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  const portfolioValue = myNfts
    .filter((n) => n.creator_id !== n.owner_id)
    .reduce((sum, n) => sum + Number(n.current_price), 0);
  const netProfit = Number(player.total_earned) - Number(player.total_spent);

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="cyber-page-bg max-w-4xl mx-auto px-4 pt-10 pb-6 md:pt-12 space-y-6"
        style={{ ['--cyber-bg' as string]: `url(${cyberBg})` }}
      >
        {/* Profile */}
        <Card className="card-game-neon">
          <CardContent className="py-4 px-4 flex items-center justify-between">
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New display name"
                  maxLength={30}
                  className="max-w-[200px] h-8 text-sm bg-black/60 border-white/10 text-white"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      updateDisplayName(newName).then((ok) => ok && setEditingName(false));
                    }
                    if (e.key === "Escape") setEditingName(false);
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 hover:bg-white/10"
                  onClick={async () => {
                    const ok = await updateDisplayName(newName);
                    if (ok) setEditingName(false);
                  }}
                >
                  <Check className="w-4 h-4 text-cyan-400" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 hover:bg-white/10" onClick={() => setEditingName(false)}>
                  <X className="w-4 h-4 text-gray-400" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-cyan-400 font-bold text-lg border border-purple-500/30">
                  {player.display_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-white">{player.display_name}</p>
                  <p className="text-xs text-gray-500">Player since {new Date(player.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            )}
            {!editingName && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-xs text-gray-400 hover:text-white hover:bg-white/10"
                onClick={() => { setNewName(player.display_name); setEditingName(true); }}
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit Name
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Buy Stars — neon banner */}
        <Card className="banner-game-neon">
          <CardContent className="py-5 px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-purple-500/40">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white text-sm" style={{ textShadow: '0 0 8px rgba(236, 72, 153, 0.5)' }}>Reload Credit</p>
                <p className="text-xs text-cyan-200/90">Reload balance with SOL</p>
              </div>
            </div>
            <Button
              onClick={() => setShowBuyStars(true)}
              className="font-bold btn-game-neon border-0"
              size="sm"
            >
              <Star className="w-4 h-4 mr-1" /> Reload
            </Button>
          </CardContent>
        </Card>

        {showBuyStars && player && (
          <BuyStarsModal
            open={showBuyStars}
            onClose={() => setShowBuyStars(false)}
            playerId={player.id}
            currentBalance={Number(player.virtual_balance) || 0}
            onSuccess={refreshPlayer}
          />
        )}
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Balance", value: `$${Number(player.virtual_balance).toFixed(2)}`, icon: Wallet },
            { label: "Trades", value: transactions.length.toString(), icon: ShoppingCart },
          ].map((s) => (
            <Card key={s.label} className="card-game-neon">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="w-4 h-4 text-pink-400" style={{ filter: 'drop-shadow(0 0 6px rgba(236, 72, 153, 0.6))' }} />
                  <span className="text-xs text-purple-300/80 font-medium">{s.label}</span>
                </div>
                <p className="text-xl font-extrabold text-white" style={{ textShadow: '0 0 10px rgba(168, 85, 247, 0.5)' }}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* My NFTs */}
        <Card className="card-game-neon">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Image className="w-5 h-5 text-pink-400" style={{ filter: 'drop-shadow(0 0 6px rgba(236, 72, 153, 0.6))' }} />
              <span style={{ textShadow: '0 0 8px rgba(168, 85, 247, 0.5)' }}>Your NFTs ({myNfts.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myNfts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">You don't own any NFTs yet</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {myNfts.map((nft) => (
                  <div key={nft.id} className="border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/30 transition-colors bg-black/40">
                    <div className="aspect-square bg-gray-800">
                      {nft.image_url ? (
                        <img src={nft.image_url} alt={nft.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl text-gray-600">🖼️</div>
                      )}
                    </div>
                    <div className="p-2.5 space-y-1.5">
                      <p className="text-sm font-bold truncate text-white">{nft.name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-extrabold text-cyan-400">${Number(nft.current_price).toFixed(2)}</span>
                        <Button
                          size="sm"
                          variant={nft.is_listed ? "outline" : "default"}
                          className={`text-xs h-7 font-bold ${
                            nft.is_listed
                              ? "border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                              : "bg-purple-500 text-black hover:bg-purple-500 border-0"
                          }`}
                          onClick={() => toggleListing(nft.id, nft.is_listed)}
                        >
                          {nft.is_listed ? "⏸ Pause" : "▶ Resume"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="card-game-neon">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <ShoppingCart className="w-5 h-5 text-pink-400" style={{ filter: 'drop-shadow(0 0 6px rgba(236, 72, 153, 0.6))' }} />
              <span style={{ textShadow: '0 0 8px rgba(168, 85, 247, 0.5)' }}>Recent Trades</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No trades yet</p>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => {
                  const isBuyer = tx.buyer_id === player.id;
                  return (
                    <div key={tx.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isBuyer ? "bg-red-500/15 text-red-400" : "bg-purple-500/15 text-cyan-400"}`}>
                          {isBuyer ? "BUY" : "SELL"}
                        </span>
                        <span className="text-sm font-semibold text-white">${Number(tx.price).toFixed(2)}</span>
                      </div>
                      <span className="text-xs text-gray-600">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </GameLayout>
  );
}
